import React from "react";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";
import { BlockMath } from "react-katex";
import Latex from "react-latex-next";

/**
 * Teoria + przykłady — Wzory skróconego mnożenia
 */
export default function WzorySkroconego_TeoriaPrzyklady() {
  return (
    <TheoryExamplesRunner
      intro={
        <>
          <p>
            Przy mnożeniu wyrażeń każdy składnik pierwszego nawiasu mnożymy przez każdy składnik drugiego, a potem redukujemy wyrazy podobne. Minus przed nawiasem zmienia znaki wszystkich jego składników.
            Wzory skróconego mnożenia pozwalają szybko rozwijać nawiasy bez
            konieczności każdorazowego wymnażania wszystkich składników.
            Poniżej pokazano, skąd się one biorą — poprzez zwykłe wymnożenie i
            redukcję wyrazów podobnych.
          </p>

          <h4 className="mt-4 font-semibold">1. Kwadrat sumy</h4>
          <BlockMath math={'(a+b)^2 = (a+b)(a+b) = a^2 + ab + ab + b^2 = a^2 + 2ab + b^2'} />
          <div className="border border-foreground/70 rounded-md p-3 mt-2 mb-4">
            <BlockMath math={'(a+b)^2 = a^2 + 2ab + b^2'} />
          </div>

          <h4 className="mt-4 font-semibold">2. Kwadrat różnicy</h4>
          <BlockMath math={'(a-b)^2 = (a-b)(a-b) = a^2 - ab - ab + b^2 = a^2 - 2ab + b^2'} />
          <div className="border border-foreground/70 rounded-md p-3 mt-2 mb-4">
            <BlockMath math={'(a-b)^2 = a^2 - 2ab + b^2'} />
          </div>

          <h4 className="mt-4 font-semibold">3. Różnica kwadratów</h4>
          <BlockMath math={'(a-b)(a+b) = a^2 + ab - ab - b^2 = a^2 - b^2'} />
          <div className="border border-foreground/70 rounded-md p-3 mt-2 mb-6">
            <BlockMath math={'(a-b)(a+b) = a^2 - b^2'} />
          </div>

          {/* Podsumowanie bez ramki */}
          <h4 className="mt-5 font-semibold">Podsumowanie</h4>
          <BlockMath math={'(a+b)^2 = a^2 + 2ab + b^2'} />
          <BlockMath math={'(a-b)^2 = a^2 - 2ab + b^2'} />
          <BlockMath math={'(a-b)(a+b) = a^2 - b^2'} />
        </>
      }

      examples={[
        {
          label: "Przykład 1", summary: "Mnożenie jednomianu przez nawias",
          description: <Latex>{`Rozwiń $3x(2x+5)$.`}</Latex>,
          content: <>
            <p><strong>Krok 1:</strong> Mnożymy jednomian przez każdy składnik nawiasu.</p>
            <BlockMath math={String.raw`3x(2x+5)=3x\cdot2x+3x\cdot5`}/>
            <p><strong>Krok 2:</strong> Mnożymy współczynniki i potęgi tej samej zmiennej.</p>
            <BlockMath math={String.raw`3x\cdot2x+3x\cdot5=6x^2+15x`}/>
          </>,
        },
        {
          label: "Przykład 2", summary: "Mnożenie dwóch nawiasów i minus przed nawiasem",
          description: <Latex>{`Rozwiń $(2x-3)(x+4)$.`}</Latex>,
          content: <>
            <p><strong>Krok 1:</strong> Każdy składnik pierwszego nawiasu mnożymy przez każdy składnik drugiego. Zachowujemy znaki.</p>
            <BlockMath math={String.raw`(2x-3)(x+4)=2x\cdot x+2x\cdot4-3\cdot x-3\cdot4`}/>
            <p><strong>Krok 2:</strong> Wykonujemy mnożenie i łączymy wyrazy z tą samą potęgą zmiennej.</p>
            <BlockMath math={String.raw`2x^2+8x-3x-12=2x^2+5x-12`}/>
            <p><strong>Krok 3:</strong> Jeżeli odejmujemy cały iloczyn, minus zmienia znaki wszystkich składników. Nawias zachowujemy do końca mnożenia.</p>
            <BlockMath math={String.raw`x^2-(2x-3)(x+4)=x^2-(2x^2+5x-12)=-x^2-5x+12`}/>
          </>,
        },

        /* 1) (ax+b)^2 — SquareOfSumLiteralTask */
        {
          label: "Przykład 3",
          summary: "Kwadrat sumy",
          description: <Latex>{`Rozwiń wyrażenie $\\big(3x + 5\\big)^2$.`}</Latex>,
          content: (
            <div className="space-y-2">
              <p><strong>Krok 1:</strong> Wzór kwadratu sumy.</p>
              <BlockMath math={'(a+b)^2 = a^2 + 2ab + b^2'} />
              <p><strong>Krok 2:</strong> Podstawiamy i liczymy:</p>
              <BlockMath math={'(3x+5)^2 = (3x)^2 + 2\\cdot(3x)\\cdot 5 + 5^2 = 9x^2 + 30x + 25'} />
            </div>
          ),
        },

        /* 2) (ax-b)^2 — SquareOfDifferenceTask */
        {
          label: "Przykład 4",
          summary: "Kwadrat różnicy",
          description: <Latex>{`Rozwiń wyrażenie $\\big(2x - 7\\big)^2$.`}</Latex>,
          content: (
            <div className="space-y-2">
              <p><strong>Krok 1:</strong> Wzór kwadratu różnicy.</p>
              <BlockMath math={'(a-b)^2 = a^2 - 2ab + b^2'} />
              <p><strong>Krok 2:</strong> Podstawiamy i liczymy:</p>
              <BlockMath math={'(2x-7)^2 = (2x)^2 - 2\\cdot(2x)\\cdot 7 + 7^2 = 4x^2 - 28x + 49'} />
            </div>
          ),
        },

        /* 3) (a-b)(a+b) — DifferenceOfSquaresTask */
        {
          label: "Przykład 5",
          summary: "Różnica kwadratów",
          description: <Latex>{`Rozwiń wyrażenie $(4x-3)(4x+3)$.`}</Latex>,
          content: (
            <div className="space-y-2">
              <p><strong>Krok 1:</strong> Wzór różnicy kwadratów.</p>
              <BlockMath math={'(a-b)(a+b) = a^2 - b^2'} />
              <p><strong>Krok 2:</strong> Podstawiamy i liczymy:</p>
              <BlockMath math={'(4x-3)(4x+3) = (4x)^2 - 3^2 = 16x^2 - 9'} />
            </div>
          ),
        },

        /* 4) (a+√m)^2 — SquareOfSumTask */
        {
          label: "Przykład 6",
          summary: "Kwadrat sumy z pierwiastkiem",
          description: <Latex>{`Oblicz $(5+\\sqrt{6})^2$.`}</Latex>,
          content: (
            <div className="space-y-2">
              <p><strong>Krok 1:</strong> Wzór kwadratu sumy.</p>
              <BlockMath math={'(a+b)^2 = a^2 + 2ab + b^2'} />
              <p><strong>Krok 2:</strong> Podstawiamy i liczymy:</p>
              <BlockMath math={'(5+\\sqrt{6})^2 = 5^2 + 2\\cdot 5 \\cdot \\sqrt{6} + (\\sqrt{6})^2 = 25 + 10\\sqrt{6} + 6 = 10\\sqrt{6} + 31'} />
            </div>
          ),
        },

        /* 5) (a-√m)^2 — SquareOfDifferenceSqrtTask */
        {
          label: "Przykład 7",
          summary: "Kwadrat różnicy z pierwiastkiem",
          description: <Latex>{`Oblicz $(4-\\sqrt{5})^2$.`}</Latex>,
          content: (
            <div className="space-y-2">
              <p><strong>Krok 1:</strong> Wzór kwadratu różnicy.</p>
              <BlockMath math={'(a-b)^2 = a^2 - 2ab + b^2'} />
              <p><strong>Krok 2:</strong> Podstawiamy i liczymy:</p>
              <BlockMath math={'(4-\\sqrt{5})^2 = 4^2 - 2\\cdot 4 \\cdot \\sqrt{5} + (\\sqrt{5})^2 = 16 - 8\\sqrt{5} + 5 = 21 - 8\\sqrt{5}'} />
            </div>
          ),
        },

        /* 6) (√r+n)(√r-n) — DifferenceOfSquaresWithRootTask */
        {
          label: "Przykład 8",
          summary: "Różnica kwadratów z pierwiastkiem",
          description: <Latex>{`Oblicz $(\\sqrt{7}+3)(\\sqrt{7}-3)$.`}</Latex>,
          content: (
            <div className="space-y-2">
              <p><strong>Krok 1:</strong> Wzór różnicy kwadratów.</p>
              <BlockMath math={'(a-b)(a+b) = a^2 - b^2'} />
              <p><strong>Krok 2:</strong> Podstawiamy i liczymy:</p>
              <BlockMath math={'(\\sqrt{7}+3)(\\sqrt{7}-3) = (\\sqrt{7})^2 - 3^2 = 7 - 9 = -2'} />
            </div>
          ),
        },
        {
          label: "Przykład 9",
          summary: "Wspólny czynnik i wzór czytany od końca",
          description: <Latex>{`Zapisz $x^3-6x^2+9x$ w postaci iloczynu.`}</Latex>,
          content: <>
            <p><strong>Krok 1:</strong> Każdy składnik zawiera czynnik x. Wyłączamy go przed nawias.</p>
            <BlockMath math={String.raw`x^3-6x^2+9x=x(x^2-6x+9)`}/>
            <p><strong>Krok 2:</strong> W nawiasie rozpoznajemy kwadrat różnicy. Wzory można stosować także od prawej do lewej.</p>
            <BlockMath math={String.raw`x^2-6x+9=x^2-2\cdot x\cdot3+3^2=(x-3)^2`}/>
            <p><strong>Krok 3:</strong> Zachowujemy wyłączony wcześniej czynnik.</p>
            <BlockMath math={String.raw`x^3-6x^2+9x=x(x-3)^2`}/>
          </>,
        },
      ]}
    />
  );
}
